import { createSlice } from '@reduxjs/toolkit';

const orderItemsSlice = createSlice({
  name: 'orderItems',
  initialState: {
    originalItems: [],
    currentItems: [],
    newlyAddedItems: [],
    deletedItems: [],
    updatedItems: []
  },
  reducers: {
    initializeItems: (state, action) => {
      const items = action.payload || [];
      state.originalItems = [...items];
      state.currentItems = [...items];
      state.newlyAddedItems = [];
      state.deletedItems = [];
      state.updatedItems = [];
    },

    addNewItems: (state, action) => {
      const newItems = action.payload || [];

      newItems.forEach(newItem => {
        // Check if item already exists in current items (by MATERIAL code)
        const existingItemIndex = state.currentItems.findIndex(
          item => item.MATERIAL === newItem.MATERIAL
        );

        if (existingItemIndex !== -1) {
          // Item already exists - update quantity instead of adding new
          const existingItem = state.currentItems[existingItemIndex];
          const newQuantity = (parseFloat(existingItem.QUANTITY) + parseFloat(newItem.QUANTITY || "1.000")).toFixed(3);

          // Update the current item quantity
          state.currentItems[existingItemIndex] = {
            ...existingItem,
            QUANTITY: newQuantity
          };

          // Check if it's a newly added item or original item
          const isNewlyAdded = state.newlyAddedItems.some(item => item.MATERIAL === newItem.MATERIAL);
          const isOriginal = state.originalItems.some(item => item.MATERIAL === newItem.MATERIAL);

          if (isNewlyAdded) {
            // Update quantity in newlyAddedItems
            const newItemIndex = state.newlyAddedItems.findIndex(item => item.MATERIAL === newItem.MATERIAL);
            if (newItemIndex !== -1) {
              state.newlyAddedItems[newItemIndex] = {
                ...state.newlyAddedItems[newItemIndex],
                QUANTITY: newQuantity
              };
            }
          } else if (isOriginal) {
            // Add to updated items if it's an original item
            const existingUpdatedIndex = state.updatedItems.findIndex(item => item.MATERIAL === newItem.MATERIAL);
            const originalItem = state.originalItems.find(item => item.MATERIAL === newItem.MATERIAL);

            if (existingUpdatedIndex !== -1) {
              // Update existing updated item
              state.updatedItems[existingUpdatedIndex] = {
                ...state.updatedItems[existingUpdatedIndex],
                QUANTITY: newQuantity
              };
            } else {
              // Add new updated item
              state.updatedItems.push({
                ...state.currentItems[existingItemIndex],
                originalQuantity: originalItem.QUANTITY
              });
            }
          }

          // IMPORTANT: If this item was previously marked for deletion, remove it from deletedItems
          const deletedItemIndex = state.deletedItems.findIndex(item => item.MATERIAL === newItem.MATERIAL);
          if (deletedItemIndex !== -1) {
            state.deletedItems.splice(deletedItemIndex, 1);
          }
        } else {
          // Item doesn't exist - add as new item
          state.newlyAddedItems.push(newItem);
          state.currentItems.push(newItem);

          // IMPORTANT: If this item was previously marked for deletion, remove it from deletedItems
          const deletedItemIndex = state.deletedItems.findIndex(item => item.MATERIAL === newItem.MATERIAL);
          if (deletedItemIndex !== -1) {
            state.deletedItems.splice(deletedItemIndex, 1);
          }
        }
      });
    },

    updateItem: (state, action) => {
      const { posnr, field, value } = action.payload;
      const itemIndex = state.currentItems.findIndex(item => item.POSNR === posnr);

      if (itemIndex !== -1) {
        // Update the current item
        state.currentItems[itemIndex] = {
          ...state.currentItems[itemIndex],
          [field]: value
        };

        // Check if it's an original item (not newly added)
        const isNewItem = state.newlyAddedItems.some(item => item.POSNR === posnr);
        const originalItem = state.originalItems.find(item => item.POSNR === posnr);

        if (!isNewItem && originalItem) {
          // It's an existing item that was updated
          const existingUpdatedIndex = state.updatedItems.findIndex(item => item.POSNR === posnr);

          if (existingUpdatedIndex !== -1) {
            // Update existing updated item
            state.updatedItems[existingUpdatedIndex] = {
              ...state.updatedItems[existingUpdatedIndex],
              [field]: value
            };
          } else {
            // Add to updated items
            state.updatedItems.push({
              ...state.currentItems[itemIndex],
              originalQuantity: originalItem.QUANTITY
            });
          }
        } else if (isNewItem) {
          // Update the newly added item
          const newItemIndex = state.newlyAddedItems.findIndex(item => item.POSNR === posnr);
          if (newItemIndex !== -1) {
            state.newlyAddedItems[newItemIndex] = {
              ...state.newlyAddedItems[newItemIndex],
              [field]: value
            };
          }
        }

        // IMPORTANT: If this item was previously marked for deletion, remove it from deletedItems
        const deletedItemIndex = state.deletedItems.findIndex(item => item.POSNR === posnr);
        if (deletedItemIndex !== -1) {
          state.deletedItems.splice(deletedItemIndex, 1);
        }
      }
    },

    deleteItem: (state, action) => {
      const posnr = action.payload;

      // Remove from current items
      state.currentItems = state.currentItems.filter(item => item.POSNR !== posnr);

      // Check if it was a newly added item
      const newItemIndex = state.newlyAddedItems.findIndex(item => item.POSNR === posnr);
      if (newItemIndex !== -1) {
        // Remove from newly added items
        state.newlyAddedItems.splice(newItemIndex, 1);
      } else {
        // It's an original item being deleted
        const originalItem = state.originalItems.find(item => item.POSNR === posnr);
        if (originalItem) {
          // Add to deleted items if not already there
          const alreadyDeleted = state.deletedItems.some(item => item.POSNR === posnr);
          if (!alreadyDeleted) {
            state.deletedItems.push(originalItem);
          }

          // Remove from updated items if it was previously updated
          state.updatedItems = state.updatedItems.filter(item => item.POSNR !== posnr);
        }
      }
    },

    resetItems: (state) => {
      state.currentItems = [...state.originalItems];
      state.newlyAddedItems = [];
      state.deletedItems = [];
      state.updatedItems = [];
    },

    clearAllItems: (state) => {
      state.originalItems = [];
      state.currentItems = [];
      state.newlyAddedItems = [];
      state.deletedItems = [];
      state.updatedItems = [];
    },

    // NEW ACTION: Remove item from deleted items when it's added back
    restoreDeletedItem: (state, action) => {
      const { material } = action.payload;
      state.deletedItems = state.deletedItems.filter(item => item.MATERIAL !== material);
    }
  }
});

export const {
  initializeItems,
  addNewItems,
  updateItem,
  deleteItem,
  resetItems,
  clearAllItems,
  restoreDeletedItem
} = orderItemsSlice.actions;

export default orderItemsSlice.reducer;
